import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import type { CardScope } from './credit'

/**
 * Krediler: eşit taksitli ödeme planı (faiz + BSMV + KKDF), masraflar ve gecikme faizi sunucuda hesaplanır.
 * Şirket kredisi "Ödeme planı" yetkileriyle yönetilir ve kullandırım/taksitler kasa-banka hareketi olarak işlenir
 * (300 Banka Kredileri, 780.03 Kredi Faiz ve Masrafları); kişisel kredi yalnız sahibine görünür ve takip içindir.
 */
/** Kredi işlem yetkileri: şirkette her biri ayrı finance:loan:* yetkisi; kişisel kredide sahibi hepsini yapar. */
export type LoanPerms = { create: boolean; pay: boolean; unpay: boolean; edit: boolean; delete: boolean }
export const OWNER_LOAN_PERMS: LoanPerms = { create: true, pay: true, unpay: true, edit: true, delete: true }

/** Kredi ya da nakit avans (aynı ödeme planı motoru, ayrı uç ve yetkiler). */
export type LoanKind = 'loan' | 'cash_advance'
export type Repayment = 'installment' | 'bullet'
const base = (kind: LoanKind) => `/api/v1/finance/${kind === 'cash_advance' ? 'cash-advances' : 'loans'}`

export type LoanFee = { name: string; amount: string; deducted: boolean }

export type LoanInstallment = {
  id: string
  no: number
  dueDate: string
  principal: string
  interest: string
  bsmv: string
  kkdf: string
  total: string
  balance: string
  status: 'pending' | 'paid'
  paidOn: string
  paidAmount: string
  lateInterest: string
  lateTax: string
  overdue: boolean
}

export type Loan = {
  id: string
  kind: LoanKind
  repayment: Repayment
  scope: CardScope
  name: string
  bankName: string
  bankId: string
  bankLogoUrl: string
  note: string
  principal: string
  monthlyRate: string
  bsmvRate: string
  kkdfRate: string
  lateRate: string
  installmentCount: number
  firstDueDate: string
  disbursedOn: string
  disbursementAccountId: string
  fees: LoanFee[]
  status: 'active' | 'closed'
  paidCount: number
  remainingPrincipal: string
  remainingTotal: string
  nextDueDate: string
  nextDueAmount: string
  overdueCount: number
  installments: LoanInstallment[]
}

export type LoanPreview = {
  installment: string
  totalInterest: string
  totalTax: string
  totalPayment: string
  feesDeducted: string
  feesSeparate: string
  netDisbursement: string
  bsmvRate: string
  kkdfRate: string
  lateRate: string
  monthlyRate: string
  plan: LoanInstallment[]
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const n = (v: unknown) => Number(v) || 0

const mapInstallment = (r: Row): LoanInstallment => ({
  id: s(r.id),
  no: n(r.no),
  dueDate: s(r.due_date),
  principal: s(r.principal),
  interest: s(r.interest),
  bsmv: s(r.bsmv),
  kkdf: s(r.kkdf),
  total: s(r.total),
  balance: s(r.balance),
  status: r.status === 'paid' ? 'paid' : 'pending',
  paidOn: s(r.paid_on),
  paidAmount: s(r.paid_amount),
  lateInterest: s(r.late_interest),
  lateTax: s(r.late_tax),
  overdue: r.overdue === true,
})

const rows = (v: unknown) => (Array.isArray(v) ? (v as Row[]) : [])

function mapLoan(r: Row): Loan {
  return {
    id: s(r.id),
    kind: r.kind === 'cash_advance' ? 'cash_advance' : 'loan',
    repayment: r.repayment === 'bullet' ? 'bullet' : 'installment',
    scope: r.scope === 'personal' ? 'personal' : 'company',
    name: s(r.name),
    bankName: s(r.bank_name),
    bankId: s(r.bank_id),
    bankLogoUrl: s(r.bank_logo_url),
    note: s(r.note),
    principal: s(r.principal),
    monthlyRate: s(r.monthly_rate),
    bsmvRate: s(r.bsmv_rate),
    kkdfRate: s(r.kkdf_rate),
    lateRate: s(r.late_rate),
    installmentCount: n(r.installment_count),
    firstDueDate: s(r.first_due_date),
    disbursedOn: s(r.disbursed_on),
    disbursementAccountId: s(r.disbursement_account_id),
    fees: rows(r.fees).map((f) => ({ name: s(f.name), amount: s(f.amount), deducted: f.deducted === true })),
    status: r.status === 'closed' ? 'closed' : 'active',
    paidCount: n(r.paid_count),
    remainingPrincipal: s(r.remaining_principal),
    remainingTotal: s(r.remaining_total),
    nextDueDate: s(r.next_due_date),
    nextDueAmount: s(r.next_due_amount),
    overdueCount: n(r.overdue_count),
    installments: rows(r.installments).map(mapInstallment),
  }
}

export const loanKeys = {
  list: (kind: LoanKind, scope: CardScope) => ['finance', 'loans', kind, scope] as const,
  detail: (kind: LoanKind, id: string) => ['finance', 'loans', kind, 'detail', id] as const,
  preview: (kind: LoanKind, scope: CardScope, body: unknown) =>
    ['finance', 'loans', kind, 'preview', scope, body] as const,
  limits: ['finance', 'loans', 'cash_advance', 'limits'] as const,
}

export type LoanInput = {
  name: string
  bankName: string
  bankId: string
  note: string
  principal: string
  /** Boşsa kredide 0, nakit avansta ayarlardaki nakit avans faizi. */
  monthlyRate: string
  /** Nakit avansta taksitli / taksitsiz (taksitsizde firstDueDate son ödeme tarihi). */
  repayment: Repayment
  /** Boşsa kapsamın kredi ayarlarındaki oran kullanılır. */
  bsmvRate: string
  kkdfRate: string
  lateRate: string
  installmentCount: number
  firstDueDate: string
  disbursedOn: string
  disbursementAccountId: string
  fees: LoanFee[]
}

const dec = (v: string) => v.trim().replace(',', '.')
const optRate = (v: string) => (v.trim() ? dec(v) : null)

export function loanBody(i: LoanInput) {
  const bullet = i.repayment === 'bullet'
  return {
    name: i.name.trim(),
    bank_name: i.bankName.trim(),
    bank_id: i.bankId,
    note: i.note.trim(),
    principal: dec(i.principal) || '0',
    monthly_rate: optRate(i.monthlyRate),
    repayment: i.repayment,
    bsmv_rate: optRate(i.bsmvRate),
    kkdf_rate: optRate(i.kkdfRate),
    late_rate: optRate(i.lateRate),
    installment_count: bullet ? 1 : i.installmentCount,
    first_due_date: i.firstDueDate,
    disbursed_on: i.disbursedOn,
    disbursement_account_id: i.disbursementAccountId,
    fees: i.fees
      .filter((f) => f.name.trim() || Number(dec(f.amount)) > 0)
      .map((f) => ({ name: f.name.trim(), amount: dec(f.amount) || '0', deducted: f.deducted })),
  }
}

export const useLoans = (kind: LoanKind, scope: CardScope, enabled = true) =>
  useQuery({
    queryKey: loanKeys.list(kind, scope),
    enabled,
    queryFn: async () => asList(await api.get<Row[]>(base(kind), { scope })).map(mapLoan),
  })

export const useLoan = (kind: LoanKind, id: string | null) =>
  useQuery({
    queryKey: loanKeys.detail(kind, id ?? ''),
    enabled: !!id,
    queryFn: async () => mapLoan(await api.get<Row>(`${base(kind)}/${id}`)),
  })

/** Kaydetmeden ödeme planı; body değiştikçe (form geçerliyse) yeniden hesaplanır. */
export const useLoanPreview = (kind: LoanKind, scope: CardScope, body: ReturnType<typeof loanBody> | null) =>
  useQuery({
    queryKey: loanKeys.preview(kind, scope, body),
    enabled: !!body,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const r = await api.post<Row>(`${base(kind)}/preview?scope=${scope}`, body)
      return {
        installment: s(r.installment),
        totalInterest: s(r.total_interest),
        totalTax: s(r.total_tax),
        totalPayment: s(r.total_payment),
        feesDeducted: s(r.fees_deducted),
        feesSeparate: s(r.fees_separate),
        netDisbursement: s(r.net_disbursement),
        bsmvRate: s(r.bsmv_rate),
        kkdfRate: s(r.kkdf_rate),
        lateRate: s(r.late_rate),
        monthlyRate: s(r.monthly_rate),
        plan: rows(r.plan).map(mapInstallment),
      } as LoanPreview
    },
  })

export type CashAdvanceLimit = {
  accountId: string
  name: string
  bankName: string
  limit: string
  used: string
  /** Hesabın eksi bakiyesi (KMH kullanımı); limit nakit avansla ortaktır. */
  kmhUsed: string
  available: string
}

/** Nakit avans limiti tanımlı şirket banka hesapları (limit, kullanılan, kalan). */
export const useCashAdvanceLimits = (enabled = true) =>
  useQuery({
    queryKey: loanKeys.limits,
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>(`${base('cash_advance')}/limits`)).map((r): CashAdvanceLimit => ({
        accountId: s(r.account_id),
        name: s(r.name),
        bankName: s(r.bank_name),
        limit: s(r.limit),
        used: s(r.used),
        kmhUsed: s(r.kmh_used),
        available: s(r.available),
      })),
  })

/** Şirket kaydı hesap bakiyelerini de etkiler: tüm finans sorguları tazelenir. */
function useLoanMut<A, R = unknown>(fn: (arg: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export const useCreateLoan = (kind: LoanKind, scope: CardScope) =>
  useLoanMut(async (i: LoanInput) => mapLoan(await api.post<Row>(`${base(kind)}?scope=${scope}`, loanBody(i))))

export const useDeleteLoan = (kind: LoanKind) => useLoanMut((id: string) => api.del(`${base(kind)}/${id}`))

export type PayInput = { date: string; accountId: string; lateInterest: string; lateTax: string }

export const usePayInstallment = (kind: LoanKind, loanId: string) =>
  useLoanMut(({ id, input }: { id: string; input: PayInput }) =>
    api.post(`${base(kind)}/${loanId}/installments/${id}/pay`, {
      date: input.date,
      account_id: input.accountId,
      late_interest: optRate(input.lateInterest),
      late_tax: optRate(input.lateTax),
    }),
  )

export const useUnpayInstallment = (kind: LoanKind, loanId: string) =>
  useLoanMut((id: string) => api.post(`${base(kind)}/${loanId}/installments/${id}/unpay`, {}))

export type InstallmentEdit = { dueDate: string; principal: string; interest: string; bsmv: string; kkdf: string }

export const useEditInstallment = (kind: LoanKind, loanId: string) =>
  useLoanMut(({ id, input }: { id: string; input: InstallmentEdit }) =>
    api.put(`${base(kind)}/${loanId}/installments/${id}`, {
      due_date: input.dueDate,
      principal: dec(input.principal) || '0',
      interest: dec(input.interest) || '0',
      bsmv: dec(input.bsmv) || '0',
      kkdf: dec(input.kkdf) || '0',
    }),
  )

/** Türe özgü metin anahtarı; nakit avansta tanımlı değilse kredi metnine düşer. */
export const textKeys = (kind: LoanKind, key: string) =>
  kind === 'cash_advance' ? [`finance.cashAdvances.${key}`, `finance.loans.${key}`] : `finance.loans.${key}`
